'use client';

import {
  useState,
  useLayoutEffect,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import FadeIn from '@/components/ui/FadeIn';
import { FeaturedReview, SmallReview } from './ReviewCard';
import StarRow from '@/components/ui/StarRow';
import { COPY } from '@/lib/copy';
import type { CmsReview } from '@/lib/contentful/types';

function ReviewsSlide({
  group,
  slideIndex,
}: {
  group: CmsReview[];
  slideIndex: number;
}) {
  const [featured, ...smalls] = group;
  const isOdd = slideIndex % 2 === 1;

  const featuredEl = <FeaturedReview review={featured} />;
  const smallsEl = (
    <div className="flex flex-col gap-4">
      {smalls.map((review, i) => (
        <SmallReview key={i} review={review} />
      ))}
    </div>
  );

  return (
    <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6 items-stretch">
      {isOdd ? (
        <>
          {smallsEl}
          {featuredEl}
        </>
      ) : (
        <>
          {featuredEl}
          {smallsEl}
        </>
      )}
    </div>
  );
}

type Props = {
  groups: CmsReview[][];
  current: number;
};

export function ReviewsCarousel({ groups, current }: Props) {
  const [height, setHeight] = useState(0);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const measureHeight = useCallback(() => {
    const el = slideRefs.current[current];
    if (el) setHeight(el.offsetHeight);
  }, [current]);

  useLayoutEffect(() => {
    measureHeight();
  }, [measureHeight]);
  useEffect(() => {
    window.addEventListener('resize', measureHeight);
    return () => window.removeEventListener('resize', measureHeight);
  }, [measureHeight]);

  return (
    <div
      className="relative overflow-hidden transition-[height] duration-300 ease-in-out"
      style={{ height }}
    >
      {groups.map((group, i) => (
        <div
          key={i}
          ref={(el) => {
            slideRefs.current[i] = el;
          }}
          className={`absolute inset-x-0 top-0 w-full transition-opacity duration-300 ${
            i === current
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden={i !== current}
        >
          <ReviewsSlide group={group} slideIndex={i} />
        </div>
      ))}
    </div>
  );
}

export function StaticReviewsSlide({ group }: { group: CmsReview[] }) {
  return (
    <FadeIn delay={0.1}>
      <ReviewsSlide group={group} slideIndex={0} />
    </FadeIn>
  );
}

export type MobileSliderHandle = { goNext: () => void; goPrev: () => void };

export const MobileReviewsSlider = forwardRef<
  MobileSliderHandle,
  { reviews: CmsReview[] }
>(function MobileReviewsSlider({ reviews }, ref) {
  const total = reviews.length;
  // Extended array: [clone of last, ...originals, clone of first]
  // trackIndex 1..total maps to real cards; 0 = clone of last, total+1 = clone of first
  const extended = [reviews[total - 1], ...reviews, reviews[0]];
  const [trackIndex, setTrackIndex] = useState(1);
  const [animated, setAnimated] = useState(true);
  const touchStartX = useRef(0);
  const busy = useRef(false); // blocks navigation while transition is running

  const goNext = useCallback(() => {
    if (busy.current) return;
    busy.current = true;
    setAnimated(true);
    setTrackIndex((i) => i + 1);
  }, []);

  const goPrev = useCallback(() => {
    if (busy.current) return;
    busy.current = true;
    setAnimated(true);
    setTrackIndex((i) => i - 1);
  }, []);

  useImperativeHandle(ref, () => ({ goNext, goPrev }), [goNext, goPrev]);

  // After transition to a clone, silently jump to the real card
  const handleTransitionEnd = useCallback(() => {
    if (trackIndex === total + 1) {
      setAnimated(false);
      setTrackIndex(1);
    } else if (trackIndex === 0) {
      setAnimated(false);
      setTrackIndex(total);
    } else {
      busy.current = false;
    }
  }, [trackIndex, total]);

  // Re-enable animation one frame after the silent jump, then release lock
  useEffect(() => {
    if (!animated) {
      const id = requestAnimationFrame(() => {
        setAnimated(true);
        busy.current = false;
      });
      return () => cancelAnimationFrame(id);
    }
  }, [animated]);

  const CARD_W = '85vw';
  const GAP = 16;

  return (
    <div
      className="overflow-hidden w-full"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        const delta = touchStartX.current - e.changedTouches[0].clientX;
        if (delta > 40) goNext();
        else if (delta < -40) goPrev();
      }}
    >
      <div
        className={`flex pb-4 ${animated ? 'transition-transform duration-300 ease-in-out' : ''}`}
        style={{
          transform: `translateX(calc(-${trackIndex} * (${CARD_W} + ${GAP}px)))`,
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {extended.map((review, i) => (
          <div
            key={i}
            aria-hidden={i === 0 || i === total + 1}
            className="shrink-0 bg-white border-l-[3px] border-primary px-5 py-5 flex flex-col justify-between"
            style={{ width: CARD_W, marginRight: GAP }}
          >
            <p className="font-sans text-[0.88rem] font-light text-color-text-mid leading-[1.75] mb-4 flex-1">
              &ldquo;{review.text}&rdquo;
            </p>
            <div className="flex items-center justify-between gap-2">
              <span className="font-sans text-[0.72rem] font-bold text-text">
                {review.authorName}
              </span>
              <span className="flex items-center gap-1">
                <StarRow count={review.rating} size={11} />
                <span className="sr-only">
                  {COPY.reviews.starsLabel(review.rating)}
                </span>
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});
